// libraries
import React, { Fragment, useState, useEffect } from "react";
import { useSelector, useDispatch, connect} from "react-redux";
import { useParams, useHistory } from "react-router-dom"
import {get, startCase, lowerCase, sortBy, get as _get} from "lodash";
import {DateTime} from "luxon";

// store and utils
import * as actions from "../../../store/actions";
import * as ODCalls from "../../../store/calls/indicativeOptionData";
import {getAssetClasses, getBusinessTypes} from "../../../store/calls";
import validators from "../../../utils/forms/validators";
import formatters from "../../../utils/forms/formatters";
import * as preconfiguredInputs from "../../../utils/forms/preconfiguredInputs"
import api from "../../../store/api";
import {store} from "../../../store"

// components
import {
    addErrorAlert,
    addSuccessAlert,
    toggleGlobalLoader,
} from "../../../store/calls/alertLoadingDispatch";
import {
    prepareApplicationFormFlow,
    pullApplication,
    updateOrCreateApplication
} from "../../../store/calls/applicationData"
import {getUUID} from "../../../utils/hashFuncs";
import FormFlowWrapper from "../../theme/wrappers/FormFlowWrapper";
import ChevronSteps from "../../theme/forms/ChevronSteps";
import SelectByCategory from "../../theme/forms/SelectByCategory";
import InfoFeed from "../../theme/cardlists/infoFeed";
import FormFlow from "../../theme/formFlows/FormFlow";
import TabsSteps from "../../theme/forms/TabsSteps";
import LoansForm from "../../theme/forms/loanAdditionForm";
import FileUploader from "../../theme/forms/FileUploader";
import ApplicationFileUpload from "./ApplicationFileUpload";
import AlertCard from "../../theme/cards/AlertCard";
import TelephoneNumber from "../../subcomponents/TelephoneNumber";
import ContactEmail from "../../subcomponents/ContactEmail";
import ConfirmModal from "../../theme/modals/ConfirmModal";
import ProjectCard from "../../subcomponents/loanEstimates/ProjectCard";


/*
*  Load routine for application, including data validity checks
*
*  1) Do we have an app id? If not, do we have a projectLead and indicative loaded in Redux when we get to this page?
*       - YES - continue
*       - NO - Show error alert telling them they are missing something, go back to either loanEStimate page if we have it or home if we're totally lost
*  2) Permissions and validity check (call to backend)
*       - Do you own this project?
*           - IF NO: We have to create a duplicate of the project for their account, and then move the system to that new ID
*       - Is there already an application associated with this project?
*           - IF YES: redirect to the application specific URL
*               - IS THE INDICATIVE THAT THEY APPLIED FOR A DIFFERENT FUNDER?
*                   - IF YES -- EXPLAIN THAT WE HAVE TO LOAD SOME NEW DOCS, THEN WE HAVE TO RESET THE FUNDER IN THE BACKEND.
*  3) Load application if we have have been called to in the URL. If we haven't, create the application with data at the end of step 1.
*
* */



/*
* Data Handling
*
* Answers are maintained in an array, with a minimum of {id, name, response}. Data comes in a {dataKey1: resp1, dataKey2: resp2} format
* It's important that we avoid updating old answers. So in writing data to this array, we first map over old ids and replace
* If it isn't there, we then append the remaining dicts to the end of response array.
*
* */

const mapDataForWrite = (applicationData, formFlowInputs) => {
    /// so all existing inputs should be in the template_elements array... otherwise client would not have input them this screen

    let existing_resp = applicationData.app?.data?.responses || [];
    let template_elements = applicationData.app.template?.elements || [];

    let completed_template_elements = Object.keys(formFlowInputs).map(key => {
        let matching_elem = template_elements.find(d => d.id === key) || {}
        return {
            id: key,
            name: matching_elem.name || key,
            response: formFlowInputs[key],
            created: new Date().toISOString()
        }
    });


    existing_resp.forEach(existing_response => {
         completed_template_elements.forEach(new_response => {
             if (new_response.id === existing_response.id) {
                 existing_response.response = new_response.response
                 if (new_response.response !== existing_response.response) {
                     existing_response.modified = new Date().toISOString()
                 }
                 new_response.inserted = true
             }
         })
    })

    // spread out new template elements, removing those that have already been inserted
    return [...existing_resp, ...completed_template_elements.filter(d => !d.inserted)]
}


const updateInputsFromAppResponses = () => {

    const state = store.getState()
    let responses = state.applicationData?.app?.data?.responses || [];

    let update_dict = {};
    responses.forEach(d => {
         update_dict[d.id] = d.response
    })
    store.dispatch(actions.formFlow.updateFormFlowInputs(update_dict))
}


const updateAppInDB = () => {

    let state = store.getState();
    let {applicationData, formFlow: {inputs}} = state;

    let data = {
        id: applicationData.app.id,
        data: {
            ...applicationData.app.data,
            responses: mapDataForWrite(applicationData, inputs),
            loans: applicationData.app.loans || []
        },
    };

    return updateOrCreateApplication(data).then(updateInputsFromAppResponses)
}

const createAppInDB = () => {

    let state = store.getState();
    let {applicationData, formFlow: {inputs}} = state;

    let data = {
        data: {
            ...applicationData.app,
            responses: mapDataForWrite(applicationData, inputs)
        },
        loan_estimate: applicationData.indicative?.id,
        project: applicationData.projectLead?.id,
    };

    toggleGlobalLoader(true);
    return updateOrCreateApplication(data).then(() => {
        prepareApplicationFormFlow();
        toggleGlobalLoader(false);
    })
}

const checkAppExistenceAndEligibility = (projectlead_id, estimate_id) => {
    if (!projectlead_id) {
        console.error("NO ELIGIBILITY CHECK. PROJECT LEAD ID MISSING.")
        return;
    }
    return api.get('/application-start-checks/', {project: projectlead_id, estimate: estimate_id})
        .then((res) => {
            if (res.data.is_project_owner && res.data.attached_application) {
                store.dispatch(actions.applicationData.setApplicationData(res.data.attached_application));
                prepareApplicationFormFlow();
            }
        })
}



/*
* Components
* */

function RequirementWarning({ title, text, actionButtonText, action = (e)=>{} }) {

    return (
        <div className="card mb-3 has-text-white has-background-danger">
            {/* <header className="card-header"> */}
            {/*    <p className="card-header-title"> */}
            {/*        {title || ''} */}
            {/*    </p> */}

            {/* </header> */}
            <div className="card-content">
                <div className="columns ">
                    <div className="column">
                        <p>
                            <i className="fas fa-exclamation-triangle mr-3"/>
                            <span>{text || ''}</span>
                        </p>
                    </div>
                    {!!actionButtonText && <div className="column is-one-fifth-desktop">
                        <div className="button is-light" onClick={action}>{actionButtonText}</div>
                    </div>}
                </div>
            </div>
        </div>
    )
}

function FatalErrorModal({msg}) {
    let focusedProjectId = useSelector(state => state.projectLeads.focusedProject)
    let userId = useSelector(state => state.user?.decodedTokenData?.user_id);

    let history = useHistory();

    let redirectionLink;
    if (!userId) {
        redirectionLink = '/'
    } else if (focusedProjectId) {
        redirectionLink = `/project-estimate/${focusedProjectId}`
    } else {
        redirectionLink = `/my-projects`
    }


    return <ConfirmModal
                visible
                type="warning"
                prompt={msg}
                onAccept={() => history.push(redirectionLink)}
            />
}

const ApplicationRedirectStatusModal  = (startCheckResult) => {
    // ingests the result of the start check, which examines if the user is the project owner, and whether an applicaiton already exists

    let history = useHistory()
    let modalConfig = {}

    // need to duplicate and apply
    if (!startCheckResult.is_project_owner) {
         modalConfig.onAccept = () => {

         }
    }


    // what happens if it's a different loan id? Need to figure that out.
    if (startCheckResult?.attachedApplication?.id) {
         modalConfig.onAccept = () => {
             history.push(`/application/${startCheckResult?.attachedApplication?.id}`)
         }
         modalConfig.type = 'info';
         modalConfig.prompt = "It looks like you already have an application open for this project. You are being redirected to work on that."
    }
}

function ApplyingForLoanEstimateCard() {
    let history = useHistory();
    let appData = useSelector(state => state.applicationData);
    let loanEstimate = appData.indicative || {};

    // this is a bit hacky, but sometimes the backend spreads the data attribute and sometimes it doesn't.
    if (Object.keys(loanEstimate?.data || {}).length) {
        loanEstimate = loanEstimate.data
    }

    let projectId =  appData.projectLead?.id || appData.app?.project;
    let attributes = loanEstimate?.attributes || []

  

    return (
        <div className="card mb-3">
            <div className="card-content">
                <div className="title is-size-4 has-text-primary">{loanEstimate?.product?.type}</div>
                <div className="table-container">
                {((loanEstimate?.product?.type === 'PPA') ?

                    <div>
                        <p className="has-text-weight-semibold">After submitting your application, your project will be bid on by our network of ppa providers.</p>
                        <p className="my-2">You will receive updates on their bids in one week via email. Once a bidder is selected, your loan officer will work with your to facilitate the loan.</p>
                    </div>

                  :  <table className="table is-striped is-fullwidth">
                        <thead>
                        <tr>
                            <th>Term</th>
                            <th>Rate</th>
                            <th>Per Payment</th>
                        </tr>
                        </thead>
                        <tbody>
                        {attributes
                            .sort((a,b) => a.term > b.term ? 1 : -1)
                            .map(d => (
                                <tr>
                                    <td>{d.term}</td>
                                    <td>{d.rate}%</td>
                                    <td>{formatters.USDCurrency.formatter(d.schedule[0].payment_w_fees)}</td>
                                </tr>
                            ))
                        }
                        </tbody>
                    </table>
                )}
                    <p className="my-2">
                        You can edit your selection by <a href={`/project-estimate/${projectId}`}>returning to the loan estimate page</a> and
                        hitting the apply button on any offering there.
                    </p>
                </div>
            </div>
        </div>
    )
}

function TemplatedFileUpload({ requiredFiles }) {

    let existingFiles = useSelector(state => state.applicationData.app.files)

    requiredFiles = requiredFiles.map(d => (
        {
            label: d.name,
            subLabel: d.comments,
            description:  d.name,
            dataKey: d.id
        }
    ))

    return (
        <div>
            <ApplicationFileUpload
                defaultFileInputs={requiredFiles}
                existingFiles={existingFiles}
            />
        </div>
    )
}

function ExistingLoanForm() {

    let existingLoans = useSelector(state => state.applicationData.app?.loans || [])

    let dispatch = useDispatch()
    let updateLoans = loans => {
         dispatch(actions.applicationData.updateApplicationData({ loans }))
    }


    const onLoanSubmit = (loan, index) => {
        let loans = existingLoans;
        if (index === 0 || index) {
            loans[index] = loan;
        } else {
            loans = [...existingLoans, loan]
        }

        loans = sortBy(loans, 'loan_seniority');
        updateLoans(loans)
    }

    const onLoanRemove = index => {
        const loans = existingLoans;
        loans.splice(index, 1);
        updateLoans(loans)
    }

    return <LoansForm
        loans={existingLoans}
        onLoanSubmit={onLoanSubmit}
        onLoanRemove={onLoanRemove}
    />
}



const generateDynamicApplyFormFlow = (applicationData, formFlowInputs, history) => {
    // testing
    let elements = applicationData?.app?.template?.elements || [];

    const getTrueInputType = question => {
        switch (question.input_type) {
            case 'long_text':
                return 'textarea'
            case 'yes_no':
                return 'select'
            case 'date':
                return 'date'
            case 'alert':
                return 'alert'
            default:
                return 'text'
        }
    }

    const getInputValidatorFunc = question => {
         if (['dollars', 'number'].includes(question.input_type)) {
             return question.required ? validators.isNumberNotNull : validators.isNumber
         } if (question.required) {
             return validators.exists
         } 
             return {}
         
    }

    const getInputFormatterFunc = question => {
        switch (question.input_type) {
            case "dollars":
                return formatters.USDCurrency
            case "number":
                return formatters.number
            default:
                return {}
        }
    }

    const getSelectOptions = question => {
        if (question.input_type === 'yes_no') {
            return preconfiguredInputs.nullBooleanSelect
        } if (Array.isArray(question.options)) {
            return {
                "type": "select",
                "options": [...question.options]
            }
        } 
            return {}
        
    }

    const generateQuestionElement = element => {
        
        return {
            ...element,
            dataKey: element.id,
            label: element.name,
            subLabel: element.comments,
            type: getTrueInputType(element),
            ...getSelectOptions(element),
            ...getInputFormatterFunc(element),
            ...getInputValidatorFunc(element),
        }
    }


    // completion functions
    const onCompleteStep = () => {
        if (!applicationData.app.id) {
            return createAppInDB(applicationData, formFlowInputs);
        } 
            return updateAppInDB(applicationData, formFlowInputs)
        
    }

    const loan_section_required = elements.some(d => d.type === 'loans')

    let qualifiers = elements.filter(d => d.type === 'qualifier')
    let questions = elements.filter(d => d.type === 'question')
    let files = elements.filter(d => d.type === 'file')


    const applicationStartMessage = (
        <div className="mb-4">
            <p>
                Application on CleanFi are divided into four parts.
            </p>
            <ol className="ml-6 my-3">
                <li>Your contact information</li>
                <li>Qualification questions, which we use to ensure that you are applying to the right product.</li>
                <li>Application questions, generated by the funder.</li>
                <li>Document uploads</li>
            </ol>
            <p >
                You can come back and finish this form at any time, and access it from the Applications tab in my projects. If you have any questions
                or doubts, don't hesitate to call us at <TelephoneNumber className="has-text-link" />!
            </p>

        </div>
    )

    return {
        title: 'Dynamic Apply FormFlow',
        onComplete: () => {
            toggleGlobalLoader(true)
            updateAppInDB(applicationData, formFlowInputs)
                .then(() => {
                    history.push('/application/' + applicationData.app.id);
                })
                .catch(() => {
                    toggleGlobalLoader(false)
                })
        },
        steps: [
            {
                title: 'Start',
                onComplete: () => {},
                aboveFormComponent: applicationStartMessage,
                inputs: [
                    {
                        type: "text",
                        dataKey: "name",
                        label: "Your Name *",
                        ...validators.isStringOfMinLength(2),
                    },
                    {
                        type: "text",
                        dataKey: "phone",
                        label: "Cell Phone *",
                        ...validators.isPhoneNumberNotNull
                    },
                    {
                        type: "text",
                        dataKey: "company",
                        label: "Company",
                    },
                    {
                        type: "select",
                        dataKey: "role",
                        label: "Role w/ Respect To Loan",
                        controlClasses: "is-expanded",
                        placeholder: "--",
                        options: [
                            {
                                value: 'borrower',
                                display: 'Borrower'
                            },
                            {
                                value: 'fiduciary',
                                display: 'Officer of Borrowing Entity',
                            },
                            {
                                value: 'contractor',
                                display: 'Contractor / Project Manager'
                            },
                            {
                                value: 'property_manager',
                                display: 'Property Manager'
                            },
                            {
                                value: 'other',
                                display: 'Other'
                            },
                        ],
                        ...validators.exists,
                    },
                ],
            },
            {
                title: "Qualifiers",
                areStepReqsMet: responses => true, // YOU SHOULD GET CHECKED HERE. MAYBE NOT BE ABLE TO ADVANCE UNTIL QUESTIONS ARE MET?
                inputs: qualifiers.map(q => generateQuestionElement(q)),
            },
            {
                title: "Questions",
                areStepReqsMet: responses => true,
                inputs: questions.map(q => generateQuestionElement(q)),
            },
            {
                title: "Documents",
                areStepReqsMet: responses => true,
                component: (
                    <TemplatedFileUpload
                        requiredFiles={files}
                    />
                )
            },
        ]
            .filter(d => Array.isArray(d.inputs) ? !!d.inputs.length : true)   // filter out empty sections
            .map(d => ({...d, onComplete: onCompleteStep }))
    }

}



function DynamicApplyFlow(props) {

    let formFlow = useSelector(state => state.formFlow);
    let userData = useSelector(state => state.userData);
    let applicationData = useSelector(state => state.applicationData)


    let [applicationFormFlow, setApplicationFormFlow] = useState({steps: []})
    let is_eligible_to_complete_app = useSelector(state => {
        let role = state.formFlow?.inputs?.role;
        return role && !['borrower', 'fiduciary'].includes(role);
    });

    let qualifier_uuids = useSelector(state => (state.applicationData?.app?.template?.elements ||[])
        .filter(d => d.type === 'qualifier')).map(d => d.id)

    let has_not_passed_requirements = useSelector(state => {
        let responses = (state.formFlow?.inputs || [])
        return qualifier_uuids.some(d => responses[d] === "false")
    })



    let [loadingErr, setLoadingErr] = useState(null);

    // null if using the /apply link, and the id if using /apply/:id link
    let {id: targetApplicationId} = useParams();
    const history = useHistory();
    const dispatch =  useDispatch();

    // loading routine
    useEffect(() => {
        toggleGlobalLoader(true);
        let promise;
        if (targetApplicationId) {
            dispatch(actions.applicationData.resetApplicationData())
            promise = pullApplication(targetApplicationId)
                .then(() => prepareApplicationFormFlow())
        } else {
            const project_lead_id = applicationData?.projectLead?.id || applicationData?.projectLead?.data?.id;
            const selected_estimate_id = applicationData?.indicative?.id;
            promise = checkAppExistenceAndEligibility(project_lead_id, selected_estimate_id)
        }
        promise
            .then((data) => {
                toggleGlobalLoader(false)
                if (data.updated_selected_estimate) {
                    addSuccessAlert({
                        msg: "It looks like the loan product you selected has been updated. We've updated the application for you. Existing work is preserved."
                    })
                }
            })
    }, [])



    useEffect(() => {
        setApplicationFormFlow(generateDynamicApplyFormFlow(applicationData, formFlow.inputs, history))
    }, [applicationData])



    // data load check
    // if (!targetApplicationId) {
    //     // no application id, so do we have a project and indicative to base this app off?
    //     if (!applicationData?.indicative?.product?.id || !applicationData.projectLead?.id) {
    //         // missing requirements
    //         setLoadingErr({
    //             msg: "It looks like we're missing some of the data we need to process this application. " +
    //                 "Please click on OK to go back and try again."
    //         })
    //     }
    // }

    // return <h1> Hello</h1>



    return (
         <FormFlowWrapper>
             <div className="form-flow-wrapper">
                 {/* WE NEED TO PUT TYPE OF FINANCING THEY ARE APPLYING FOR HERE TO GIVE THEM SOME CONTEXT */}
                 <h2 className="is-size-2 has-text-primary mb-4 only-desktop">
                     Financing Application
                 </h2>
                 <ChevronSteps
                     stepCount={(applicationFormFlow?.steps || []).length}
                     renderStepContent={stepNum => {
                         const labels = applicationFormFlow?.steps.map((d,i) => d.title || i + 1)
                         return labels[stepNum -1]
                     }}
                     activeStep={formFlow.currStep}
                     onStepClick={stepNum => dispatch(actions.formFlow.setFormFlowCurrStep(stepNum))}
                 />
                 {/* {is_eligible_to_complete_app &&
                     <RequirementWarning
                         text={<span>
                             You must have signing power for the borrower to complete this application. Share this project
                             with the borrower <a href={`/project-estimate/${applicationData.projectLead?.id}`} className="is-underlined has-text-white">from the loan estimate page</a> to have them apply.
                            </span>}
                         actionButtonText="Share"
                         action={() => window.location = `/project-estimate/${applicationData.projectLead?.id}`}
                     />
                 } */}
                 {has_not_passed_requirements &&
                     <RequirementWarning
                         text={<span>You have not answered yes to all of the qualifying questions. Please select a different financing
                     product or call  us at <TelephoneNumber className="has-text-link"/> so we can help.</span>}
                         actionButtonText="Return to Options"
                         action={() => window.location = `/project-estimate/${applicationData.projectLead?.id}`}
                     />
                 }
                 <div className="columns only-desktop">
                     <div className="column is-three-fifths">
                         <FormFlow
                             {...applicationFormFlow}
                         />
                     </div>
                     <div className="column pl-3">
                         <div className="pl-5">
                             <div className="form-section-header">Applying For</div>
                             <div className="mb-3">
                                <ProjectCard project={applicationData.projectLead}/>
                             </div>
                             <ApplyingForLoanEstimateCard />
                             <div className="card mb-3">
                                 <header className="card-header">
                                     <p className="card-header-title">
                                          Need help? Call us.
                                     </p>

                                 </header>
                                 <div className="card-content">
                                     <div>
                                         <TelephoneNumber
                                             className="has-text-success is-size-3 has-text-semibold mb-3"
                                         />
                                     </div>
                                     <div>
                                         <ContactEmail
                                             className="has-text-grey-dark is-size-6 has-text-semibold"
                                         />
                                     </div>
                                 </div>
                             </div>
                         </div>
                     </div>
                 </div>
                 {!!loadingErr &&
                     <FatalErrorModal
                         {...loadingErr}
                     />
                 }
             </div>
         </FormFlowWrapper>
    )
}

export default DynamicApplyFlow;
